import {Injectable} from '@angular/core';
import {ApiHelperService, ApiResult} from './api-helper.service';
import {API} from 'aws-amplify';
import {OverlayService} from './overlay.service';
import {MessageDialogComponent} from '../../views/partials/message-dialog/message-dialog.component';
import {AuthService} from './auth.service';
import {MatDialog} from '@angular/material/dialog';
import {CouponDistributionConditionType} from '../model/resources/coupon';
import {ActivityType} from '../model/resources/activity';
import {GeoJson} from '../model/geo-json';

@Injectable({
  providedIn: 'root'
})
export class CouponApiService {

  constructor(
    private apiHelperService: ApiHelperService,
    private authService: AuthService,
    private dialog: MatDialog,
    private overlayService: OverlayService,
  ) {
  }

  async create(createCouponInput: CreateCouponInput): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: createCouponInput
    };

    try {
      const result = await this.overlayService.call(API.post(this.apiHelperService.apiName, 'coupons', init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  async update(id: number, updateInformationInput: UpdateCouponInput): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: updateInformationInput
    };

    try {
      const result = await this.overlayService.call(API.put(this.apiHelperService.apiName, `coupons/${id}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  async getList(raceId?: number): Promise<ApiResult> {
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init: any = {
      headers
    };

    if (raceId) {
      init.queryStringParameters = {
        raceId
      };
    }

    try {
      const result = await this.overlayService.call(API.get(this.apiHelperService.apiName, `coupons`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  async getById(id: number): Promise<ApiResult> {
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };

    try {
      const result = await this.overlayService.call(API.get(this.apiHelperService.apiName, `coupons/${id}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  async deleteById(id: number): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };

    try {
      const result = await this.overlayService.call(API.del(this.apiHelperService.apiName, `coupons/${id}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }
}

export interface CreateCouponInput {
  title: string;
  raceId?: number;
  description: string;
  objectShop: string;
  notes: string;
  confirmation: string;
  qrCodeImageId: string;
  qrCodeImageWidth: number;
  qrCodeImageHeight: number;
  qrCodeImageFileName: string;
  qrCodeImageContentType: string;
  qrCodeImageContentSize: number;
  expirationDate: number;
  beginningDistributionTerm: number;
  endOfDistributionTerm: number;
  distributionConditionType: CouponDistributionConditionType;
  distributionConditionValue: number;
  effectiveTime: number;
  usePushNotification: boolean;
  pushNotificationMessage: string;
  distributionAllUserCouponSettings?: DistributionAllUserCouponSettings;
  specifiedArea?: GeoJson;
}

export interface UpdateCouponInput {
  title: string;
  description: string;
  objectShop: string;
  notes: string;
  confirmation: string;
  qrCodeImageId: string;
  qrCodeImageWidth: number;
  qrCodeImageHeight: number;
  qrCodeImageFileName: string;
  qrCodeImageContentType: string;
  qrCodeImageContentSize: number;
  expirationDate: number;
  beginningDistributionTerm: number;
  endOfDistributionTerm: number;
  distributionConditionType: CouponDistributionConditionType;
  distributionConditionValue: number;
  effectiveTime: number;
  usePushNotification: boolean;
  pushNotificationMessage: string;
  distributionAllUserCouponSettings?: DistributionAllUserCouponSettings;
  specifiedArea?: GeoJson;
}

export interface DistributionAllUserCouponSettings {
  targetActivityTypes: ActivityType[];
  targetYearMonth: number;
}
